import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Footer } from "../components/landing/Footer";
import { Header } from "../components/landing/Header";
import { PreviewLoadingCard } from "../components/PreviewLoadingCard";
import { SearchHandleForm } from "../components/landing/SearchHandleForm";
import { ProfilePreviewCard } from "../components/ProfilePreviewCard";
import { landingContent } from "../data/landingContent";
import { useProfileSearch } from "../hooks/useProfileSearch";
import { t } from "../i18n";

const previewLoadingSteps = [
  "searchLoading1",
  "searchLoading2",
  "searchLoading3",
  "searchLoading4",
  "searchLoading5",
];

export function SearchPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { state, search, reset } = useProfileSearch();
  const handle = searchParams.get("handle") ?? "";
  const { hero } = landingContent;
  const isSearching = state.phase === "validating" || state.phase === "loading";
  const showSearchHero = state.phase !== "success" && !isSearching;

  useEffect(() => {
    if (handle) {
      search(handle);
    }
    return () => reset();
  }, [handle]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="st-page st-preview-page">
      <Header />
      <main>
        {showSearchHero && (
          <section id="top" className="st-preview-hero" aria-label="Profile preview search">
            <div className="st-preview-blob st-preview-blob-a" aria-hidden="true" />
            <div className="st-preview-blob st-preview-blob-b" aria-hidden="true" />
            <div className="st-container st-preview-hero-inner">
              <p className="st-preview-kicker">{t("searchPageKicker")}</p>
              <h1>{hero.title}</h1>
              <p className="st-preview-subtitle">
                {t("searchPageSubtitle")}
              </p>
              <div className="st-search-wrapper st-preview-search-wrap">
                <SearchHandleForm
                  placeholder={hero.inputPlaceholder}
                  ctaLabel={hero.ctaLabel}
                  typewriterExamples={[]}
                  initialValue={handle}
                />
              </div>
            </div>
          </section>
        )}

        <section
          className={`st-section st-preview-results ${
            state.phase === "success" ? "st-preview-success-view" : ""
          } ${isSearching ? "st-preview-loading-view" : ""
          }`}
          aria-live="polite"
        >
          {state.phase === "success" && (
            <>
              <div className="st-preview-blob st-preview-blob-a" aria-hidden="true" />
              <div className="st-preview-blob st-preview-blob-b" aria-hidden="true" />
            </>
          )}
          <div className="st-container">
            {!handle && (
              <article className="st-preview-state-card">
                <h2>{t("searchPageEnterHandle")}</h2>
                <p>{t("searchPageEnterHandleDesc")}</p>
              </article>
            )}

            {state.phase === "validating" && (
              <PreviewLoadingCard
                handle={handle}
                progress={0.08}
                steps={previewLoadingSteps}
              />
            )}

            {state.phase === "loading" && (
              <PreviewLoadingCard
                handle={handle}
                progress={state.progress}
                steps={state.steps}
                profilePicUrl={state.data?.profile?.profilePicUrl}
              />
            )}

            {state.phase === "success" && (
              <ProfilePreviewCard
                data={state.data}
                onStartMonitoring={() => {
                  navigate(`/paywall?handle=${encodeURIComponent(handle)}`);
                }}
                onClose={() => {
                  reset();
                  navigate("/");
                }}
              />
            )}

            {state.phase === "error" && (
              <article className="st-preview-state-card st-preview-error-card">
                <h2>{t("commonError")}</h2>
                <p>{state.message}</p>
                <div className="st-preview-error-actions">
                  <button onClick={() => search(handle)} className="st-preview-ghost-button">
                    {t("commonRetry")}
                  </button>
                </div>
              </article>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
